import type { GrChartSeriesColor } from '../types/chart-theme.type';
import { STACK_COLOR_FAMILIES } from './stack-color-families.data';
import type {
  DashboardChartGranularity,
  DashboardCustomChartSeriesDef,
  DashboardCustomChartSeriesType,
} from '../types/dashboard-layout.type';

export interface CustomTableDefinition {
  readonly key: string;
  readonly label: string;
  readonly description: string;
  readonly icon: string;
}

export const CUSTOM_TABLE_CATALOG: ReadonlyArray<CustomTableDefinition> = [
  {
    key: 'recent-reviews',
    label: 'Recent Reviews',
    description: 'Latest guest reviews across all sources.',
    icon: 'rate_review',
  },
  {
    key: 'service-tickets',
    label: 'Service Tickets',
    description: 'Open and recently resolved service tickets.',
    icon: 'support_agent',
  },
  {
    key: 'top-guests',
    label: 'Top Guests',
    description: 'Most active and highest-value guests.',
    icon: 'people',
  },
  {
    key: 'review-sources',
    label: 'Review Sources',
    description: 'Breakdown of review volume by platform.',
    icon: 'public',
  },
  {
    key: 'guest-list',
    label: 'Guest List',
    description: 'All guests with name, room, arrival, departure and current status.',
    icon: 'person',
  },
  {
    key: 'report-list',
    label: 'Reports',
    description: 'Saved reports with name, type, tags and visibility.',
    icon: 'summarize',
  },
];

export const CUSTOM_TABLE_INDEX: Readonly<Record<string, CustomTableDefinition>> =
  CUSTOM_TABLE_CATALOG.reduce(
    (acc, def) => ({ ...acc, [def.key]: def }),
    {} as Record<string, CustomTableDefinition>,
  );

export interface CustomTableColumnOption {
  readonly label: string;
  readonly value: string;
}

export const CUSTOM_TABLE_RAW_COLUMN_OPTIONS: ReadonlyArray<CustomTableColumnOption> = [
  { label: 'Guest',       value: 'guest' },
  { label: 'Name',        value: 'name' },
  { label: 'Room',        value: 'room' },
  { label: 'Date',        value: 'date' },
  { label: 'Check-in',    value: 'checkin' },
  { label: 'Check-out',   value: 'checkout' },
  { label: 'Rating',      value: 'rating' },
  { label: 'Source',      value: 'source' },
  { label: 'Status',      value: 'status' },
  { label: 'Category',    value: 'category' },
  { label: 'Subject',     value: 'subject' },
  { label: 'Department',  value: 'department' },
  { label: 'Assignee',    value: 'assignee' },
  { label: 'Priority',    value: 'priority' },
  { label: 'Notes',       value: 'notes' },
  { label: 'Value',       value: 'value' },
  { label: 'Tags',        value: 'tags' },
];

export const CUSTOM_TABLE_AGGREGATE_COLUMN_OPTIONS: ReadonlyArray<CustomTableColumnOption> = [
  { label: 'Group',           value: 'group' },
  { label: 'Period',          value: 'period' },
  { label: 'Source',          value: 'source' },
  { label: 'Department',      value: 'department' },
  { label: 'Status',          value: 'status' },
  { label: 'Category',        value: 'category' },
  { label: 'Count',           value: 'count' },
  { label: 'Avg. Rating',     value: 'avg_rating' },
  { label: 'Total',           value: 'total' },
  { label: 'Min',             value: 'min' },
  { label: 'Max',             value: 'max' },
  { label: 'Percentage (%)',  value: 'pct' },
  { label: 'Score',           value: 'score' },
  { label: 'Rank',            value: 'rank' },
  { label: 'Growth (%)',      value: 'growth' },
  { label: 'Response Rate',   value: 'response_rate' },
];

export interface CustomChartSeedSeries {
  readonly name: string;
  readonly color: GrChartSeriesColor | string;
  readonly data: ReadonlyArray<number | null>;
  readonly chartType: DashboardCustomChartSeriesType;
  readonly yAxisIndex: 0 | 1;
  readonly markerShape?: string;
}

export interface CustomChartSeed {
  readonly categories: ReadonlyArray<string>;
  readonly series: ReadonlyArray<CustomChartSeedSeries>;
  readonly xAxisLabel?: string;
}

const SERIES_DATA_TYPES: Readonly<Record<string, 'percentage' | 'count'>> = {
  gr_rating:            'percentage',
  review_count:         'count',
  online_review_rating: 'percentage',
  online_review_count:  'count',
  feedback_rating:      'percentage',
  feedback_count:       'count',
  mgmt_response_count:  'count',
  sentiment_rating:     'percentage',
  rating:               'percentage',
  count:                'count',
  rating_pct:           'percentage',
  nps_score:            'percentage',
  nps_count:            'count',
};

const DATE_AXIS_KEYS = new Set<string>(['checkin_date', 'review_date', 'survey_date', 'departure_date']);

const GRANULARITY_LABELS: Record<DashboardChartGranularity, ReadonlyArray<string>> = {
  day:     ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  week:    ['Wk 1', 'Wk 2', 'Wk 3', 'Wk 4', 'Wk 5', 'Wk 6', 'Wk 7', 'Wk 8'],
  month:   ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  quarter: ['Q1', 'Q2', 'Q3', 'Q4'],
  year:    ['2021', '2022', '2023', '2024', '2025'],
};

const PROPERTY_CATEGORIES: ReadonlyArray<string> = [
  'The Grand Hotel', 'Seaside Resort', 'Mountain Lodge', 'City Centre Hotel', 'Boutique Suites',
];

const KPI_CATEGORIES: ReadonlyArray<string> = [
  'Overall Satisfaction', 'Cleanliness', 'Service Received',
  'Value for Money', 'Location', 'Sleep Quality', 'Room Quality', 'Food Quality',
];

const KPI_GROUP_CATEGORIES: Readonly<Record<string, ReadonlyArray<string>>> = {
  'kpi:primary_service_rating': [
    'Overall Satisfaction', 'Value For Money', 'Would Recommend', 'Service Received',
    'Cleanliness', 'Location', 'Sleep Quality', 'Room Quality', 'Food Quality',
    'Housekeeping', 'Atmosphere', 'Breakfast Service',
  ],
  'kpi:room_quality': [
    'Room Comfort & Decor', 'Cleanliness', 'Housekeeping',
    'Maintenance', 'Facilities & Amenities',
  ],
  'kpi:restaurant_and_bar': [
    'Comfort & Decor', 'Cleanliness', 'Menu Choice',
    'Wine & Beverage', 'Waiter Service', 'Waiting Time', 'Food Quality',
  ],
  'kpi:spa_and_gym': [
    'Cleanliness', 'Treatment Choice', 'Gym Equipment',
    'Sauna', 'Treatment Quality', 'Therapist Skill', 'Value for Money', 'Staff Friendliness',
  ],
};

const SOURCE_PLATFORM_CATEGORIES: ReadonlyArray<string> = [
  'GuestRevu', 'TripAdvisor', 'Google', 'Booking.com', 'Expedia', 'Airbnb',
];

const KEYWORD_CATEGORIES: ReadonlyArray<string> = [
  'staff', 'cleanliness', 'breakfast', 'location', 'room', 'pool', 'check-in', 'value',
];

const AXIS_LABELS: Record<string, string> = {
  checkin_date:    'Check-in Date',
  review_date:     'Review Date',
  survey_date:     'Survey Date',
  departure_date:  'Departure Date',
  source_platform: 'Source Platform',
  property:        'Property',
  kpi_names:       'KPI',
  top_keywords:    'Keyword',
  'kpi:primary_service_rating': 'Primary Service Rating',
  'kpi:room_quality':           'Room Quality',
  'kpi:restaurant_and_bar':     'Restaurant & Bar',
  'kpi:spa_and_gym':            'Spa & Gym',
};

const SERIES_LABELS: Record<string, string> = {
  gr_rating:            'GuestRevu Rating',
  review_count:         'Review Count',
  online_review_rating: 'Online Review Rating',
  online_review_count:  'Online Review Count',
  feedback_rating:      'Guest Feedback Rating',
  feedback_count:       'Guest Feedback Count',
  mgmt_response_count:  'Management Response Count',
  sentiment_rating:     'Sentiment Rating',
  rating:               'Rating',
  count:                'Count',
  rating_pct:           'Rating (%)',
  overall_rating:       'Rating (%)',
  response_count:       'Count',
  nps_score:            'NPS Score',
  nps_count:            'NPS Count',
  avg_score:            'Rating (%)',
};

const SERIES_COLORS: ReadonlyArray<GrChartSeriesColor> = [
  'brand', 'success', 'info', 'warning', 'positive',
];

function seededRandom(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 1664525 + 1013904223) & 0xffffffff;
    return (s >>> 0) / 0xffffffff;
  };
}

function hashString(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

function generateSeries(
  name: string,
  count: number,
  colorIndex: number,
  seed: number,
): { name: string; color: GrChartSeriesColor; data: ReadonlyArray<number | null> } {
  const rand = seededRandom(seed);
  const data = Array.from({ length: count }, () => Math.round(rand() * 80 + 20));
  return { name, color: SERIES_COLORS[colorIndex % SERIES_COLORS.length], data };
}

const GROUP_BY_SERIES: Record<string, ReadonlyArray<string>> = {
  source_platform: ['GuestRevu', 'TripAdvisor', 'Booking.com', 'Google'],
  property:        ['The Grand Hotel', 'Seaside Resort', 'Mountain Lodge', 'City Centre Hotel', 'Boutique Suites'],
  top_keywords:    ['staff', 'cleanliness', 'breakfast', 'location', 'room', 'pool'],
  nps_breakdown:   ['Promoters', 'Passives', 'Detractors'],
  review_type:         ['Online Reviews', 'Guest Feedback'],
  sentiment_breakdown: ['Positive', 'Neutral', 'Negative'],
};

function categoriesForAxis(xAxis: string, granularity: DashboardChartGranularity): ReadonlyArray<string> {
  if (DATE_AXIS_KEYS.has(xAxis)) return GRANULARITY_LABELS[granularity] ?? GRANULARITY_LABELS.month;
  if (xAxis === 'source_platform') return SOURCE_PLATFORM_CATEGORIES;
  if (xAxis === 'property')        return PROPERTY_CATEGORIES;
  if (xAxis === 'kpi_names')       return KPI_CATEGORIES;
  if (xAxis === 'top_keywords')    return KEYWORD_CATEGORIES;
  if (KPI_GROUP_CATEGORIES[xAxis]) return KPI_GROUP_CATEGORIES[xAxis];
  return GRANULARITY_LABELS.month;
}

export function buildCustomChartSeed(
  xAxis: string,
  seriesDefs: ReadonlyArray<DashboardCustomChartSeriesDef>,
  groupBy: string | undefined,
  granularity: DashboardChartGranularity,
): CustomChartSeed {
  const categories = categoriesForAxis(xAxis, granularity);
  const xAxisLabel = (AXIS_LABELS[xAxis] ?? xAxis) || undefined;

  let series: ReadonlyArray<CustomChartSeedSeries>;

  if (groupBy && seriesDefs[0]) {
    const primaryDef = seriesDefs[0];
    const groupNames = GROUP_BY_SERIES[groupBy] ?? ['Group A', 'Group B'];
    const primarySeed = hashString(`${xAxis}:${primaryDef.measure}:${groupBy}:${granularity}`);
    const primaryYAxis: 0 | 1 = SERIES_DATA_TYPES[primaryDef.measure] === 'count' ? 1 : 0;
    const colorFamily = primaryDef.stackColorFamilyId
      ? STACK_COLOR_FAMILIES.find((f) => f.id === primaryDef.stackColorFamilyId)
      : undefined;

    const primarySeries: CustomChartSeedSeries[] = groupNames.map((name, i) => {
      const generated = generateSeries(name, categories.length, i, primarySeed + i);
      return {
        ...generated,
        ...(colorFamily ? { color: colorFamily.swatches[i % colorFamily.swatches.length] } : {}),
        chartType: primaryDef.chartType,
        yAxisIndex: primaryYAxis,
      };
    });

    const overlaySeries: CustomChartSeedSeries[] = seriesDefs.slice(1).map((def, i) => {
      const seed = hashString(`${xAxis}:${def.measure}:${groupBy}:${granularity}`);
      const name = SERIES_LABELS[def.measure] ?? def.measure;
      const yAxisIndex: 0 | 1 = SERIES_DATA_TYPES[def.measure] === 'count' ? 1 : 0;
      const generated = generateSeries(name, categories.length, groupNames.length + i, seed);
      return {
        ...generated,
        ...(def.color ? { color: def.color } : {}),
        ...(def.markerShape ? { markerShape: def.markerShape } : {}),
        chartType: def.chartType,
        yAxisIndex,
      };
    });

    series = [...primarySeries, ...overlaySeries];
  } else {
    series = seriesDefs.map((def, i) => {
      const baseSeed = hashString(`${xAxis}:${def.measure}:${groupBy ?? ''}:${granularity}`);
      const name = SERIES_LABELS[def.measure] ?? def.measure;
      const yAxisIndex: 0 | 1 = SERIES_DATA_TYPES[def.measure] === 'count' ? 1 : 0;
      const generated = generateSeries(name, categories.length, i, baseSeed);
      return {
        ...generated,
        ...(def.color ? { color: def.color } : {}),
        ...(def.markerShape ? { markerShape: def.markerShape } : {}),
        chartType: def.chartType,
        yAxisIndex,
      };
    });
  }

  return { categories, series, xAxisLabel };
}
